import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  OutputRefSubscription,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import {
  jsonDeepClone,
  OutsideClickDirective,
  SiaI18nService,
  SiaParamsAndPatternsService,
  SiaSelectBoxDataService,
  SiaSessionParam,
  SiaSessionService,
  SiaTooltipDirective,
  SiaTranslatePipe,
} from '@sia/shared';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subscription } from 'rxjs';
import { SelectBoxOptions } from '../../interfaces/select-box-options';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';
import { SiaErrorHintComponent } from '../sia-error-hint/sia-error-hint.component';

@Component({
  selector: 'sia-select-box',
  templateUrl: './select-box.component.html',
  styleUrls: ['./select-box.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgxMatSelectSearchModule,
    OutsideClickDirective,
    SiaErrorHintComponent,
    ReactiveFormsModule,
    SiaTooltipDirective,
    SiaTranslatePipe,
    ScrollingModule,
    ClipboardModule,
    MatIconButton,
    MatFormField,
    FormsModule,
    MatButton,
    MatOption,
    MatSelect,
    MatLabel,
    MatHint,
    MatIcon,
  ],
})
export class SiaSelectBoxComponent extends SiaBaseEditorSignalComponent {
  // Private services
  #widgetDataService = inject(SiaSelectBoxDataService);
  #sessionService = inject(SiaSessionService);
  #SiaParamsAndPatternsService = inject(SiaParamsAndPatternsService);
  #clipboard = inject(Clipboard);
  #siaI18nService = inject(SiaI18nService);
  readonly isAdmin = signal<boolean | undefined>(undefined);
  selectFilterCtrl: FormControl = new FormControl();
  readonly showAll = signal<boolean>(false);
  keepClosed: boolean = false;
  readonly emptyElementLabel = input<string>('');
  readonly scriptButtonEmptyResearch = input<string>('');
  readonly scriptButtonEmptyResearchParams = input<any>({});
  readonly autoSelectFirst = input<boolean>(false);
  readonly selectBoxOptionsInput = input<SelectBoxOptions>(
    {},
    { alias: 'selectBoxOptions' },
  );
  readonly fixedDataSourceInput = input<any[] | undefined>(undefined, {
    alias: 'fixedDataSource',
  });
  readonly foreignKeyInput = input<any>(undefined, { alias: 'foreignKey' });
  // Private state
  readonly #selectBoxOptions = signal<SelectBoxOptions>({});
  readonly selectBoxOptions = computed(() => this.#selectBoxOptions());
  #dataSource: any[] = [];
  readonly #dataSourceVersion = signal(0);
  readonly isMobile = signal<boolean | null | undefined>(undefined);
  #subs: (Subscription | OutputRefSubscription)[] = [];
  #timeouts: any[] = [];
  readonly isOpen = signal<boolean>(false);
  #inputString: string = '';

  #destroyed = false;
  readonly filteredDataSource = signal<any[]>([]);
  readonly showVisibilityButton = signal<boolean>(false);
  dataSourceLoaded = output<boolean>();
  #dataSourceLoaded = signal<boolean>(false);
  #dataSourceLoadedEffect = effect(() => {
    this.dataSourceLoaded.emit(this.#dataSourceLoaded());
  });
  #disabledCloseEffect = effect(() => {
    if (this.isDisabled()) {
      this.selectbox?.close();
    }
  });

  readonly effectiveAutoSelectFirst = computed(() => {
    this.autoSelectFirst();
    this.selectBoxOptions();
    var selectFirst = this.autoSelectFirst()
      ? this.autoSelectFirst()
      : (this.selectBoxOptions().autoSelectFirst ?? false);
    return selectFirst;
  });
  readonly #selectBoxOptionsEffect = effect(() => {
    const incomingOptions = this.selectBoxOptionsInput() ?? {};
    const incomingFixed =
      incomingOptions.fixedDataSource ?? this.fixedDataSourceInput();
    const fixedClone = incomingFixed ? jsonDeepClone(incomingFixed) : undefined;
    const foreignKey = incomingOptions.foreignKey ?? this.foreignKeyInput();
    const previous = untracked(() => this.#selectBoxOptions());
    const merged: SelectBoxOptions = {
      translateData: true,
      orderByKey: incomingOptions.orderByKey ?? 'description',
      ...incomingOptions,
      fixedDataSource: fixedClone,
      foreignKey,
      autoSelectFirst:
        incomingOptions.autoSelectFirst ?? this.autoSelectFirst() ?? false,
    };
    const needReload =
      JSON.stringify(previous.fixedDataSource ?? []) !==
        JSON.stringify(merged.fixedDataSource ?? []) ||
      previous.dbKey !== merged.dbKey ||
      previous.foreignKey !== merged.foreignKey;

    this.#selectBoxOptions.set(merged);
    this.value.set(merged.value);
    untracked(() => {
      if (needReload) {
        this.#dataSourceLoaded.set(false);
        this.loadDataSource();
      } else if (!this.#dataSourceLoaded()) {
        // Preserve already-loaded state; do not block processing when options are unchanged
        this.#dataSourceLoaded.set(true);
      }
    });
  });
  autoSelectedFirstEvent = output<string | undefined>();
  @ViewChild(MatSelect, { static: false }) selectbox?: MatSelect;
  #hostElement = inject(ElementRef<HTMLElement>);
  #scrollHandler: (event: Event) => void = () => {};
  #scrollTargets: EventTarget[] = [];
  readonly #scrollListenerCapture = true;
  #selectPanelElement?: HTMLElement | null;
  #visualViewportHeightBaseline?: number;
  #closeSuppressedUntil = 0;
  readonly #keyboardViewportDiffThreshold = 80;
  readonly #isIOS =
    typeof navigator !== 'undefined' &&
    /iP(ad|hone|od)/i.test((navigator.userAgent ?? '').toLowerCase());
  // Track selected visibility state relative to filtered/viewport
  readonly isSelectedInFiltered = signal<boolean>(false);
  readonly isSelectedRendered = signal<boolean>(false);
  #viewportSub?: Subscription;
  #lastCopyTime: number = 0;
  readonly #copyCoolDownMs: number = 500;

  readonly MAX_VIEWPORT_HEIGHT = 209;
  readonly viewportItemSize = 35;
  private isSearching(): boolean {
    const v = this.selectFilterCtrl?.value;
    return typeof v === 'string' && v.trim().length > 0;
  }
  get viewportHeight(): number {
    const len = this.filteredDataSource().length ?? 0;
    if (!len) return 0;
    let total = len * this.viewportItemSize;
    if (
      ((!this.selectFilterCtrl.value &&
        !this.selectBoxOptions().disableEmptyElement) ||
        (!this.filteredDataSource().length &&
          this.scriptButtonEmptyResearch() !== '')) &&
      total < this.MAX_VIEWPORT_HEIGHT
    ) {
      total += 35;
    }
    return Math.min(total, this.MAX_VIEWPORT_HEIGHT);
  }
  private setFilteredDataSource(value: any[]): void {
    let next: any[] = value ? value.slice() : [];
    const selectedValue = this.selectedValue();

    if (
      !this.showAll() &&
      value &&
      value.length > 0 &&
      this.showVisibilityButton()
    ) {
      next = value.filter((x) => !x.disabled);
      if (selectedValue?.id && !next.some((x) => x.id == selectedValue?.id)) {
        next = next.concat([selectedValue]);
      }
    }

    this.filteredDataSource.set(next ?? []);
    setTimeout(() => {
      try {
        this.cdkVirtualScrollViewPort?.checkViewportSize();
        this.scrollSelectedIntoView();
      } catch {}
    }, 0);
    this.updateSelectedPresence();
  }
  signalDataSource = signal<any[]>([]);
  get dataSource(): any[] {
    return this.#dataSource;
  }
  set dataSource(value: any[]) {
    const newValueJson = JSON.stringify(value);
    const currentValueJson = JSON.stringify(this.#dataSource);
    this.signalDataSource.set(value);
    if (currentValueJson === newValueJson) {
      this.processDataSource();
      return;
    }

    // Clear previous data completely to help garbage collection
    this.clearDataSource();
    if (
      this.value() &&
      typeof this.value() === 'string' &&
      typeof value[0]?.id === 'number'
    ) {
      try {
        this.value.set(parseInt(this.value()));
      } catch (error) {}
    }
    // Set new data with proper array cloning
    this.#dataSource = value ? value.slice() : [];
    this.#dataSourceVersion.update((v) => v + 1);

    if (value && value.length > 0 && value[0]?.hasOwnProperty('disabled')) {
      this.showVisibilityButton.set(
        Object.keys(value[0]).includes('disabled') &&
          typeof value[0]['disabled'] === 'boolean',
      );
    } else {
      this.showVisibilityButton.set(false);
    }
    this.processDataSource();
  }

  private clearDataSource(): void {
    // Clear arrays properly to help garbage collection
    if (this.#dataSource) {
      this.#dataSource.length = 0;
    }
    this.#dataSource = [];
    this.#dataSourceVersion.update((v) => v + 1);
    this.filteredDataSource.set([]);
    this.showVisibilityButton.set(false);
  }

  @ViewChild(CdkVirtualScrollViewport, { static: false })
  cdkVirtualScrollViewPort?: CdkVirtualScrollViewport;

  private scrollSelectedIntoView(pad: number = 2) {
    if (!this.cdkVirtualScrollViewPort || this.isSearching()) return;
    const filtered = this.filteredDataSource();
    if (!filtered || filtered.length === 0) return;
    if (this.value() == null) return;
    let index = filtered.findIndex((x) => x?.id == this.value());
    if (index < 0) return;
    if (index >= pad) index = index - pad;
    try {
      this.cdkVirtualScrollViewPort.checkViewportSize();
      this.cdkVirtualScrollViewPort.scrollToIndex(index, 'auto');
      this.cdkVirtualScrollViewPort.checkViewportSize();
    } catch {
      // ignore
    }
  }

  #selectionDescriptionChanged = effect(() => {
    this.signalDataSource();
    this.value();
    if (!this.#dataSourceLoaded()) return;
    let currentValue = this.value();
    let currentDescription = this.signalDataSource().find((x) => {
      return x?.id == currentValue;
    })?.description;
    if (
      !this.value() ||
      (this.value() && this.signalDataSource().length > 0 && currentDescription)
    ) {
      this.selectionDescriptionChanged.emit(currentDescription);
    }
  });

  private updateSelectedPresence(): void {
    const data = this.filteredDataSource() ?? [];
    this.isSelectedInFiltered.set(
      !!this.value() && data.some((x) => x?.id == this.value()),
    );
    const selectedValue = this.selectedValue();

    // If panel isn't open or viewport not available, it's not rendered
    const vp = this.cdkVirtualScrollViewPort;
    if (!this.isOpen() || !vp) {
      this.isSelectedRendered.set(false);

      return;
    }
    const index = data.findIndex((x) => x?.id == this.value());
    if (index < 0) {
      this.isSelectedRendered.set(false);
      return;
    }
    try {
      const range = (vp as any).getRenderedRange?.();
      if (
        range &&
        typeof range.start === 'number' &&
        typeof range.end === 'number'
      ) {
        this.isSelectedRendered.set(index >= range.start && index < range.end);
        if (data.length == range.end && this.dataSource.length > range.end) {
          this.isSelectedRendered.set(!!selectedValue && !!selectedValue.id);
        }
        return;
      }
    } catch {}
    this.isSelectedRendered.set(false);
  }

  private bindViewportScroll(): void {
    // Unbind previous
    if (this.#viewportSub) {
      try {
        this.#viewportSub.unsubscribe();
      } catch {}
      this.#viewportSub = undefined;
    }
    const vp = this.cdkVirtualScrollViewPort;
    if (!vp) return;
    this.#viewportSub = vp.elementScrolled().subscribe(() => {
      this.updateSelectedPresence();
    });
    this.#subs.push(this.#viewportSub);
    // Initial compute
    this.updateSelectedPresence();
  }

  selectedIdsChange = output<any[]>();
  selectedDataChange = output<any>();

  selectionChanged = output<{
    previousId: any;
    currentId: any;
  }>();
  selectionDescriptionChanged = output<string | undefined>();
  filterElements() {
    if (!this.#dataSource || this.#dataSource.length === 0) {
      this.setFilteredDataSource([]);
      return;
    }

    const filterValue = this.selectFilterCtrl.value;
    if (!filterValue || filterValue.trim() === '') {
      this.setFilteredDataSource(this.#dataSource);
      return;
    }

    const lowerFilterValue = filterValue.toLowerCase();
    this.setFilteredDataSource(
      this.#dataSource.filter(
        (item) =>
          item.description &&
          item.description.toLowerCase().includes(lowerFilterValue),
      ),
    );
  }

  loadDataSource(force: boolean = false) {
    const options = this.selectBoxOptions();
    if (options.fixedDataSource && !this.#dataSourceLoaded()) {
      this.#dataSourceLoaded.set(true);
      this.dataSource = options.fixedDataSource;
    } else if (options.dbKey) {
      this.#widgetDataService
        .loadFullData(
          options.dbKey,
          options.foreignKey,
          false,
          false,
          options.useCache,
          force,
          options.orderByKey,
          options.orderByAsc,
          options.connectionStringName,
        )
        .then((res) => {
          res = res.filter((x: any) => x.description);
          this.#dataSourceLoaded.set(true);
          var newDataSource: any;
          if (options.removeUser) {
            newDataSource = res?.filter(
              (x: any) => x.id != this.siaUsersService.getUserData()?.id,
            );
          } else {
            newDataSource = res;
          }
          if (
            JSON.stringify(this.#dataSource) != JSON.stringify(newDataSource)
          ) {
            this.dataSource = newDataSource;
          }
        });
    }
  }

  processDataSource() {
    const options = this.selectBoxOptions();
    const autoSelectFirst = this.effectiveAutoSelectFirst();
    if (
      !this.#dataSourceLoaded() ||
      (!options.dbKey && !options.fixedDataSource)
    ) {
      return;
    }

    // Reset keepClosed state if conditions no longer apply
    if (
      this.keepClosed &&
      (!options.disableEmptyElement ||
        !autoSelectFirst ||
        this.#dataSource?.length !== 1)
    ) {
      this.keepClosed = false;
    }
    if (
      this.value() &&
      (options.removeNotFoundValue || autoSelectFirst) &&
      this.#dataSource.find((x) => x.id == this.value()) == undefined
    ) {
      // If the value is not found in the data source, reset it
      let value: any;
      if (autoSelectFirst) {
        value = this.#dataSource[0];
      } else {
        value = undefined;
      }
      this.value.set(value?.id);
      this.valueChanged.emit(value?.id);
      this.selectedDataChange.emit(value);
      if (autoSelectFirst) {
        this.autoSelectedFirstEvent.emit(value?.description);
      }
    } else if (
      this.value() &&
      this.#dataSource.find((x) => x.id == this.value())
    ) {
      let value = this.#dataSource.find((x) => x.id == this.value());
      this.valueChanged.emit(value?.id);
      this.selectedDataChange.emit(value);
    }

    if (!this.#dataSource || this.#dataSource.length == 0) {
      this.selectedDataChange.emit(undefined);
      this.setFilteredDataSource(this.#dataSource);
      this.cdr.detectChanges();
      return;
    }
    if (this.value()) {
      const description = options.translateData
        ? this.#siaI18nService.translate(
            this.selectedValue()?.description ?? '',
          )
        : this.selectedValue()?.description;
      // this.selectedDataChange.emit(this.selectedValue());
    }
    // Auto-select first when enabled and no valid value is present, but only if there are 3 or fewer items
    const dataLen = this.#dataSource.length;
    const hasValue =
      this.value() !== undefined &&
      this.value() !== null &&
      this.value() !== '';
    const valueNotFound = hasValue
      ? this.#dataSource.find((x) => {
          if (typeof x?.id === 'string' && typeof this.value() === 'string') {
            return x.id.toLowerCase() === String(this.value()).toLowerCase();
          }
          return x?.id == this.value();
        }) === undefined
      : false;

    // Special case: if disableEmptyElement and autoSelectFirst are both true, and there's exactly 1 record,
    // automatically select it and disable the select-box
    if (
      options.disableEmptyElement &&
      autoSelectFirst &&
      dataLen === 1 &&
      (!hasValue || valueNotFound)
    ) {
      const first = this.#dataSource[0];
      this.value.set(first?.id);
      this.selectedDataChange.emit(first);
      this.valueChanged.emit(this.value());
      const description = options.translateData
        ? this.#siaI18nService.translate(first?.description)
        : first?.description;
      this.keepClosed = true;
      if (autoSelectFirst) {
        this.autoSelectedFirstEvent.emit(description);
      }

      try {
        const escapeForCss = (value: string) => {
          if (!value) {
            return value;
          }
          const cssEscaper = (window as any).CSS?.escape;
          if (typeof cssEscaper === 'function') {
            return cssEscaper(value);
          }
          return value.replace(/([^\w-])/g, (match) => `\\${match}`);
        };

        // Prefer jQuery removal if available
      } catch {}
    } else if (
      autoSelectFirst &&
      dataLen > 0 &&
      (!hasValue || valueNotFound) &&
      !this.isDisabled()
    ) {
      const first = this.#dataSource[0];
      this.value.set(first?.id);
      this.selectedDataChange.emit(first);
      this.valueChanged.emit(this.value());
      const description = options.translateData
        ? this.#siaI18nService.translate(first?.description)
        : first?.description;
      if (autoSelectFirst) {
        this.autoSelectedFirstEvent.emit(description);
      }
    } else if (autoSelectFirst && dataLen > 0 && hasValue && !valueNotFound) {
      this.autoSelectedFirstEvent.emit(
        this.#dataSource.find((x) => x.id == this.value())?.description,
      );
    }
    this.setFilteredDataSource(this.#dataSource);
    const t = setTimeout(() => this.scrollSelectedIntoView(), 0);
    this.#timeouts.push(t);
    this.#dataSourceVersion.update((v) => v + 1);
  }

  execScriptButtonEmptyResearch() {
    if (this.scriptButtonEmptyResearch()) {
      this.execScript(
        this.scriptButtonEmptyResearch(),
        this.scriptButtonEmptyResearchParams(),
      );
    }
  }

  updateSqlView() {
    this.siaRouterService.navigateMenuWithParams('popup^db-editor', [
      { id: 'sqlObject', value: this.selectBoxOptions().dbKey },
    ]);
  }

  // Adapter for [(ngModel)] with signal-backed value
  onNgModelChange(newValue: any): void {
    this.selectValueChanged(newValue);
    this.value.set(newValue);
  }

  selectValueChanged(newValue: any) {
    if (!this.#dataSource) return;
    if (
      typeof newValue === 'string' &&
      this.#dataSource &&
      this.#dataSource.length > 0 &&
      typeof this.#dataSource[0].id === 'number'
    ) {
      try {
        newValue = parseInt(newValue);
        this.value.set(newValue);
      } catch (error) {}
    }
    const value = this.#dataSource.find((x) => x.id == newValue);
    const previousId = this.selectedValue()?.id;

    // Only proceed if the value actually changed
    if (previousId === value?.id) return;

    this.selectedDataChange.emit(value);
    this.selectionChanged.emit({
      currentId: value?.id,
      previousId: previousId,
    });

    const options = this.selectBoxOptions();
    if (options.sessionKey) {
      this.#sessionService.updateSessionParams(options.guid, [
        {
          id: options.sessionKey + '-description',
          value: value?.description,
        },
      ]);
    }
    const description = options.translateData
      ? this.#siaI18nService.translate(value?.description)
      : value?.description;
    if (this.cdr) {
      try {
        this.cdr.markForCheck();
      } catch (error) {
        // Component might be destroyed, ignore the error
      }
    }
    const t = setTimeout(() => this.scrollSelectedIntoView(), 0);
    this.#timeouts.push(t);
    // Update presence flags after selection change
    this.updateSelectedPresence();
  }
  openChange($event: boolean) {
    if (this.keepClosed) {
      this.closeSelectOptions();
      return;
    }
    this.#inputString = '';
    this.cleanupScrollHandler();
    this.isOpen.set($event);

    if (!this.#dataSource || this.#dataSource.length == 0) {
      this.loadDataSource();
    }
    if (
      !this.selectedValue() &&
      !this.value() &&
      this.cdkVirtualScrollViewPort
    ) {
      this.cdkVirtualScrollViewPort?.scrollToIndex(0);
    }
    if ($event) {
      // Compute presence immediately on open
      this.updateSelectedPresence();
      // Bind viewport scroll updates when opening (no delay)
      this.bindViewportScroll();
      // Then try to place the selection into view
      const t = setTimeout(() => this.scrollSelectedIntoView(), 0);
      this.#timeouts.push(t);
      // Only add event listener when opening
      const timeoutId = setTimeout(() => {
        if (this.#destroyed || !this.selectbox) return;

        const overlay = document.querySelector(
          '.cdk-overlay-container .cdk-overlay-connected-position-bounding-box',
        );
        if (overlay) {
          const selectPanel = overlay.querySelector(
            '.mat-mdc-select-panel',
          ) as HTMLElement | null;
          if (selectPanel) {
            // Find the backdrop that belongs to this specific select overlay
            const overlayPane = overlay.parentElement;
            const backdrop = overlayPane?.querySelector(
              '.cdk-overlay-backdrop:not(.cdk-overlay-dark-backdrop)',
            ) as HTMLElement;
            if (backdrop && overlayPane?.contains(selectPanel)) {
              // Only hide the select backdrop, not dialog backdrops
              backdrop.style.display = 'none';
            }

            this.#setupExternalScrollHandling(selectPanel);
          }
        }
      }, 0);
      this.#timeouts.push(timeoutId);
    } else {
      // When closing, ensure cleanup
      this.closeSelectOptions();
    }
  }
  private cleanupScrollHandler(): void {
    const capture = this.#scrollListenerCapture;
    this.#scrollTargets.forEach((target) => {
      try {
        target.removeEventListener('scroll', this.#scrollHandler, capture);
      } catch {}
    });
    this.#scrollTargets = [];
    this.#selectPanelElement = undefined;
    this.#visualViewportHeightBaseline = undefined;
    this.#closeSuppressedUntil = 0;
  }

  #setupExternalScrollHandling(selectPanel: HTMLElement): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.#selectPanelElement = selectPanel;
    const viewport = window.visualViewport;
    this.#visualViewportHeightBaseline = viewport?.height ?? window.innerHeight;
    this.#closeSuppressedUntil = 0;

    const hostElement = this.#hostElement.nativeElement;
    const candidates = this.#getScrollableAncestors(hostElement);
    const uniqueTargets = new Set<EventTarget>(candidates);

    if (typeof document !== 'undefined') {
      uniqueTargets.add(document);
      const scrollingElement = document.scrollingElement;
      if (scrollingElement) {
        uniqueTargets.add(scrollingElement);
      }
    }
    uniqueTargets.add(window);

    this.#scrollTargets = Array.from(uniqueTargets);

    const listenerOptions = {
      capture: this.#scrollListenerCapture,
      passive: true,
    } as const;

    this.#scrollHandler = (event: Event) => {
      if (!this.isOpen()) {
        return;
      }
      if (this.#isKeyboardScrollEvent(event)) {
        return;
      }
      if (!this.#shouldHandleExternalScroll(event)) {
        return;
      }
      if (this.#isCloseSuppressed()) {
        return;
      }
      this.closeSelectOptions();
    };

    this.#scrollTargets.forEach((target) => {
      try {
        target.addEventListener('scroll', this.#scrollHandler, listenerOptions);
      } catch {}
    });
  }

  #getScrollableAncestors(element: HTMLElement): EventTarget[] {
    const results: EventTarget[] = [];
    if (typeof document === 'undefined') {
      return results;
    }

    let current: HTMLElement | null = element.parentElement;
    while (current) {
      if (this.#isElementScrollable(current)) {
        results.push(current);
      }
      if (current === document.body || current === document.documentElement) {
        break;
      }
      current = current.parentElement;
    }

    return results;
  }

  #isElementScrollable(element: HTMLElement): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const style = window.getComputedStyle(element);
    const overflowValues = [style.overflow, style.overflowY, style.overflowX];
    const allowsScroll = overflowValues.some((value) =>
      ['auto', 'scroll', 'overlay'].includes(value),
    );
    if (!allowsScroll) {
      return false;
    }
    return (
      element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth
    );
  }

  onSearchFocus(): void {
    this.#suppressCloseTemporarily(this.getSuppressionDuration());
  }

  private getSuppressionDuration(): number {
    return this.#isIOS ? 1500 : 200;
  }

  #suppressCloseTemporarily(
    durationMs: number = this.getSuppressionDuration(),
  ): void {
    const expiry = Date.now() + durationMs;
    this.#closeSuppressedUntil = expiry;
    const timeout = setTimeout(() => {
      if (this.#closeSuppressedUntil === expiry) {
        this.#closeSuppressedUntil = 0;
      }
    }, durationMs);
    this.#timeouts.push(timeout);
  }

  #isCloseSuppressed(): boolean {
    return Date.now() < this.#closeSuppressedUntil;
  }

  #shouldHandleExternalScroll(event: Event): boolean {
    const target = event.target as EventTarget | null;

    if (!target) {
      return true;
    }

    if (target instanceof HTMLElement) {
      if (
        this.#selectPanelElement &&
        this.#selectPanelElement.contains(target)
      ) {
        return false;
      }
      if (
        this.#isIOS &&
        typeof document !== 'undefined' &&
        this.#isFocusInsideSelectPanel() &&
        (target === document.body || target === document.documentElement) &&
        this.#shouldIgnoreDocumentScroll()
      ) {
        return false;
      }
      const host = this.#hostElement.nativeElement;
      return target.contains(host);
    }

    if (typeof window !== 'undefined' && target === window) {
      return !this.#shouldIgnoreDocumentScroll();
    }

    if (typeof Document !== 'undefined' && target instanceof Document) {
      return !this.#shouldIgnoreDocumentScroll();
    }

    return true;
  }

  #shouldIgnoreDocumentScroll(): boolean {
    if (!this.#isIOS) {
      return false;
    }
    if (typeof window === 'undefined') {
      return false;
    }
    if (!this.#isFocusInsideSelectPanel()) {
      return false;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return false;
    }

    const currentHeight = viewport.height;
    if (this.#visualViewportHeightBaseline === undefined) {
      this.#visualViewportHeightBaseline = currentHeight;
      return false;
    }

    const diff = Math.abs(currentHeight - this.#visualViewportHeightBaseline);
    this.#visualViewportHeightBaseline = currentHeight;
    return diff > this.#keyboardViewportDiffThreshold;
  }

  #isFocusInsideSelectPanel(): boolean {
    if (typeof document === 'undefined') {
      return false;
    }
    const panel = this.#selectPanelElement;
    if (!panel) {
      return false;
    }
    const active = document.activeElement;
    if (!active) {
      return false;
    }
    return panel.contains(active);
  }

  #isKeyboardScrollEvent(event: Event): boolean {
    if (!this.#isIOS) {
      return false;
    }
    if (!this.#isFocusInsideSelectPanel()) {
      return false;
    }
    if (!this.#shouldIgnoreDocumentScroll()) {
      return false;
    }

    if (event.target instanceof HTMLElement) {
      const host = this.#hostElement.nativeElement;
      if (event.target === host || host.contains(event.target)) {
        return true;
      }
    }

    if (typeof window !== 'undefined' && event.target === window) {
      return true;
    }

    if (typeof document !== 'undefined') {
      if (
        event.target === document.body ||
        event.target === document.documentElement
      ) {
        return true;
      }
    }

    if (typeof Document !== 'undefined' && event.target instanceof Document) {
      return true;
    }

    return true;
  }

  closeSelectOptions(): void {
    this.isOpen.set(false);
    this.selectbox?.close();

    // Clean up event listeners
    this.cleanupScrollHandler();

    // Clean up viewport subscription
    if (this.#viewportSub) {
      try {
        this.#viewportSub.unsubscribe();
      } catch {}
      this.#viewportSub = undefined;
    }

    // Update presence flags after closing
    const t = setTimeout(() => {
      this.updateSelectedPresence();
    }, 0);
    this.#timeouts.push(t);
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Tab' || e.key === 'Alt') {
      return;
    }
    if ((e.ctrlKey || (e as any).metaKey) && (e.key === 's' || e.key === 'S')) {
      return;
    }
    if (this.isOpen()) {
      // Refresh suppression while typing so layout shifts do not close the panel.
      this.#suppressCloseTemporarily(this.getSuppressionDuration());
    }
    if (e.ctrlKey && e.key === 'Backspace') {
      this.#inputString = '';
    }

    if ((e.ctrlKey || (e as any).metaKey) && (e.key === 'c' || e.key === 'C')) {
      const target = e.target as HTMLElement | null;
      const tag = (target?.tagName || '').toLowerCase();
      const isEditable = (target as any)?.isContentEditable === true;
      if (tag === 'input' || tag === 'textarea' || isEditable) {
        return; // let native copy happen inside editable fields
      }
      const text = this.selectedValue()?.description ?? '';
      if (text) {
        try {
          this.#clipboard.copy(text);
        } catch {
          try {
            (navigator as any)?.clipboard?.writeText?.(text);
          } catch {}
        }
        this.#lastCopyTime = Date.now();
        e.preventDefault();
        e.stopPropagation();
      }
      return; // don't run type-ahead logic on Ctrl+C
    }

    // Ignore pure modifier keys to avoid clearing selection (e.g., holding Ctrl)
    if (e.key === 'Control' || e.key === 'Meta' || e.key === 'Shift') {
      return;
    }

    // During cooldown after copy, if Ctrl/Meta still pressed, do nothing
    if (
      (e.ctrlKey || (e as any).metaKey) &&
      Date.now() - this.#lastCopyTime < this.#copyCoolDownMs
    ) {
      return;
    }
    if (e.key === 'Backspace') {
      // remove the last character from the string
      this.#inputString = this.#inputString.slice(0, -1);
    } else if (e.key.length === 1) {
      // Concatenate the character to the string
      this.#inputString += e.key;
      const filtered = this.filteredDataSource();
      let value = filtered.find((x: any) =>
        x.description.toLowerCase().startsWith(this.#inputString.toLowerCase()),
      )?.id;
      if (!value) {
        value = filtered.find((x: any) =>
          x.description.toLowerCase().includes(this.#inputString.toLowerCase()),
        )?.id;
      }
      if (value) {
        this.value.set(value);
        this.selectValueChanged(value);
      }
    }
  }
  onBlur(e: FocusEvent) {
    this.#inputString = '';
  }

  showRadar() {
    this.navigateRadar(
      this.selectBoxOptions().gridId ?? '',
      this.selectBoxOptions().gridId,
      [],
      null,
      this.value(),
    ).then((data) => {
      if (!data) {
        return;
      }
      this.value.set(data.id);
    });
  }

  async navigateRadar(
    menuId: string,
    menuMobileId?: string,
    params: SiaSessionParam[] = [],
    foreignKey?: any,
    selectedId?: any,
  ): Promise<any> {
    params.push({ id: 'selectionMode', value: 'single' });

    if (selectedId) {
      params.push({
        id: 'selectedids',
        value: [JSON.stringify({ id: selectedId })],
      });
    }
    if (foreignKey) {
      params.push({ id: '__foreignkey', value: foreignKey });
    }
    if (menuMobileId && this.siaSizeDetector.currentSize.isMobile) {
      this.siaRouterService.navigateMenuWithParams(
        'radar^' + menuMobileId,
        params,
      );
    } else {
      this.siaRouterService.navigateMenuWithParams('radar^' + menuId, params);
    }

    this._radarOpen = true;
    var promiseResolve: any, promiseReject;
    this._radarSelection = new Promise(function (resolve, reject) {
      promiseResolve = resolve;
      promiseReject = reject;
    });
    this.promiseResolver = promiseResolve;

    return this._radarSelection;
  }

  ngAfterViewInit() {
    let s0 = this.selectFilterCtrl.valueChanges.subscribe(() => {
      this.filterElements();
      if (this.isOpen()) {
        this.#suppressCloseTemporarily(this.getSuppressionDuration());
        // Reset the active item to the first one (the search box option)
        // so that the next Down arrow key correctly moves to the first result.
        const tFocus = setTimeout(() => {
          (this.selectbox as any)?._keyManager?.setActiveItem(0);
        }, 1);
        this.#timeouts.push(tFocus);
      }
      // After filtering, keep current selection visible
      const t = setTimeout(() => {
        this.cdkVirtualScrollViewPort?.checkViewportSize();
        this.scrollSelectedIntoView();
      }, 0);
      this.#timeouts.push(t);
      // And recompute presence flags
      const t2 = setTimeout(() => this.updateSelectedPresence(), 0);
      this.#timeouts.push(t2);
    });
    this.#subs.push(s0);
    // Initial compute
    this.updateSelectedPresence();
  }
  ngOnDestroy(): void {
    // Mark as destroyed first
    this.#destroyed = true;

    // Close the select box if open to trigger cleanup
    this.selectbox?.close();

    // Clean up event listeners first
    this.cleanupScrollHandler();

    // Unsubscribe from all subscriptions
    if (this.#subs && this.#subs.length > 0) {
      this.#subs.forEach((sub) => {
        try {
          sub.unsubscribe();
        } catch (e) {
          // Ignore unsubscribe errors
        }
      });
    }
    this.#subs = [];

    // Clear all timeouts
    if (this.#timeouts && this.#timeouts.length > 0) {
      this.#timeouts.forEach((timeout) => {
        try {
          clearTimeout(timeout);
        } catch (e) {
          // Ignore clear timeout errors
        }
      });
    }
    this.#timeouts = [];

    // Clear data arrays completely to help garbage collection
    this.clearDataSource();

    // Clean up form control properly
    if (this.selectFilterCtrl) {
      try {
        this.selectFilterCtrl.reset();
        // Don't destroy the form control as it might be used elsewhere
      } catch (e) {
        // Ignore form control cleanup errors
      }
    }

    // Clear radar selection promise
    if (this.promiseResolver) {
      try {
        this.promiseResolver(null);
      } catch (e) {
        // Ignore promise resolver errors
      }
    }
    this._radarSelection = undefined;
    this._radarOpen = false;

    // Clear input string
    this.#inputString = '';

    // Reset flags
    this.#dataSourceLoaded.set(false);
    this.showAll.set(false);
    this.showVisibilityButton.set(false);

    // Call parent destroy
    super.ngOnDestroy();
  }
  ngOnInit() {
    var promiseResolve: any, promiseReject;
    this._radarSelection = new Promise(function (resolve, reject) {
      promiseResolve = resolve;
      promiseReject = reject;
    });
    this.promiseResolver = promiseResolve;
    this.isMobile.set(this.siaSizeDetector.currentSize.isMobile);
    this.isAdmin.set(this.siaUsersService.getUserData()?.isAdmin);
    let s0 = this.valueChanged.subscribe((value) => {
      if (value && value != this.selectedValue()?.id) {
        this.selectValueChanged(value);
      }
    });
    this.#subs.push(s0);
    let s1 = this.siaSignalrService.refreshCollectionsMessage$.subscribe(
      (messageKeys: { [key: string]: boolean }) => {
        const options = this.selectBoxOptions();
        if (!options.dbKey) return;

        const dbKeyLower = options.dbKey.toLowerCase();
        const messageKeysLower = Object.keys(messageKeys).map((key) =>
          key.toLowerCase(),
        );

        if (
          messageKeysLower.some((key: string) => dbKeyLower.includes(key)) ||
          messageKeys['*']
        ) {
          // Clear the filtered view and force reload
          this.#dataSourceLoaded.set(false);
          this.loadDataSource();
        }
      },
    );
    this.#subs.push(s1);

    let s = this.#SiaParamsAndPatternsService.departmentChanged$.subscribe(
      () => {
        const options = this.selectBoxOptions();
        if (!options.dbKey) return;

        this.clearDataSource();
        this.#dataSourceLoaded.set(false);
        this.loadDataSource(true);
      },
    );
    this.#subs.push(s);

    let s2 = this.siaRadarService
      .finalSelectionChanged$()
      .subscribe((selection: any) => {
        if (this._radarOpen) {
          this.promiseResolver(selection);
          this._radarOpen = false;
        }
      });
    this.#subs.push(s2);
    let s3 = this.siaRouterService.routeChanged$.subscribe(() => {
      this.selectbox?.close();
    });
    this.#subs.push(s3);
  }
  toggleVisibility() {
    this.showAll.update((current) => !current);
    this.filterElements();
  }
  readonly selectedValue = computed<
    { id: string; description: string } | undefined
  >(() => {
    this.#dataSourceVersion();
    return this.#dataSource.find((x) => x.id == this.value());
  });
  readonly shouldShowSelectedValue = computed(
    () => !!this.selectedValue() && !this.isOpen(),
  );

  trackById(index: number, item: any): any {
    // Use item.id if available, otherwise fallback to index
    // This helps Angular optimize re-rendering of mat-option elements
    return item?.id !== undefined ? item.id : index;
  }
}
